import { useEffect, useState } from 'react'
import type { CombatDTO, PersonnageDTO } from '@whatremains/shared'
import { api } from '../api'
import { Engager } from '../ecrans/Engager'

/**
 * L'onglet « Se battre » : il charge le personnage, puis laisse `Engager` faire.
 *
 * ## Pourquoi une couche de plus, et pourquoi elle est mince
 *
 * Enzo : « le menu Se battre ne devrait pas se trouver dans personnage, mais
 * dans son propre onglet. » Il a raison, et la place qu'il occupait le disait
 * mal : la fiche décrit ce qu'on EST, se battre est quelque chose qu'on FAIT.
 *
 * Mais `Engager` reçoit le personnage en propriété, parce qu'il était toujours
 * ouvert depuis un écran qui l'avait déjà chargé — la fiche, ou une étape de
 * quête. Un onglet n'a rien chargé du tout.
 *
 * Deux façons de régler ça, et la différence compte. Rendre la propriété
 * facultative dans `Engager` et le faire charger lui-même aurait mis DEUX
 * chemins dans un écran qui n'en avait qu'un, pour le confort d'un seul de ses
 * appelants. Ce fichier fait le chargement à sa place et lui passe ce qu'il a
 * toujours attendu : `Engager` ne sait rien de cette histoire, et les trois
 * chemins d'accès continuent de lui parler dans la même langue.
 *
 * ## Ce qu'il ne fait pas
 *
 * Pas de `cibleImposee`. Un adversaire imposé vient d'une étape de quête, qui
 * ouvre l'écran par son propre chemin ; l'onglet, lui, ouvre toujours la liste
 * complète. Et pas de `surSortie` : on est dans un onglet, on en sort en
 * cliquant un autre onglet, pas par un bouton qui rendrait la main à un écran
 * dont on ne vient pas.
 */
export function EntreeCombat({
  id,
  surEngage,
}: {
  id: string
  surEngage: (c: CombatDTO) => void
}) {
  const [p, setP] = useState<PersonnageDTO | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  useEffect(() => {
    let vivant = true
    api
      .personnage(id)
      .then((x) => { if (vivant) setP(x) })
      .catch((e: unknown) => {
        if (vivant) setErreur(e instanceof Error ? e.message : 'Impossible de te charger.')
      })
    // Le drapeau évite d'écrire dans un composant démonté quand on change
    // d'onglet avant la fin de la requête.
    return () => { vivant = false }
  }, [id])

  if (erreur) return <section><p className="erreur">{erreur}</p></section>
  // Rien plutôt qu'un squelette : la requête dure quelques dizaines de
  // millisecondes, et un écran qui clignote coûte plus qu'un écran vide.
  if (!p) return null

  return <Engager personnage={p} surEngage={surEngage} surSortie={() => {}} />
}
